import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Send } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sandtech-hub-monorepo/ui-kit';
import { GetViewerTranscriptQuery } from '@sandtech-hub-monorepo/ehub-client';
import {
  TranscriptView,
  TranscriptCourseEnrollment,
  TranscriptConductCase,
  TranscriptProgramEnrollment,
  TranscriptTermGPA,
} from '@sandtech-hub-monorepo/ui-kit-site';
import RequestOfficialTranscriptModal, {
  RequestPrefill,
} from './RequestOfficialTranscriptModal';
import TranscriptRequestsStatus from './TranscriptRequestsStatus';

type ProgramEnrollment =
  GetViewerTranscriptQuery['viewer']['programEnrollments'][number];

type ClassEnrollment = NonNullable<
  ProgramEnrollment['plannedCourses'][number]['enrollment']
>['classEnrollments'][number];

interface Props {
  viewer: GetViewerTranscriptQuery['viewer'];
  programEnrollments: ProgramEnrollment[];
}

const collectCourseEnrollments = (
  programEnrollments: ProgramEnrollment[]
): TranscriptCourseEnrollment[] => {
  const seen = new Set<string>();
  const result: TranscriptCourseEnrollment[] = [];

  for (const pe of programEnrollments) {
    for (const pc of pe.plannedCourses) {
      if (!pc.enrollment) continue;
      if (seen.has(pc.enrollment.id)) continue;
      seen.add(pc.enrollment.id);
      result.push({
        status: pc.enrollment.status ?? '',
        letterGrade: pc.enrollment.letterGrade,
        gradePointsValue: pc.enrollment.gradePointsValue,
        graduatedAt: pc.enrollment.graduatedAt,
        failedAt: pc.enrollment.failedAt,
        course: {
          name: pc.details.name,
          abbreviation: pc.details.abbreviation,
        },
        classEnrollments: pc.enrollment.classEnrollments
          .filter((cle: ClassEnrollment) => !cle.cancelled)
          .map((cle: ClassEnrollment) => ({
            letterGrade: cle.letterGrade,
            gradePointsValue: cle.gradePointsValue,
            status: cle.status,
            graduatedAt: cle.graduatedAt,
            failedAt: cle.failedAt,
            class: {
              term: cle.class?.term ?? null,
              startDate: cle.class?.startDate ?? null,
              name: cle.class?.name ?? '',
            },
          })),
      });
    }
  }

  return result;
};

const buildCreditMap = (
  programEnrollments: ProgramEnrollment[]
): Map<string, number> => {
  const credits = new Map<string, number>();
  for (const pe of programEnrollments) {
    for (const pc of pe.plannedCourses) {
      if (!credits.has(pc.details.abbreviation)) {
        credits.set(pc.details.abbreviation, pc.credits);
      }
    }
  }
  return credits;
};

const TranscriptContent = ({ viewer, programEnrollments }: Props) => {
  const navigate = useNavigate();
  // Persist the active tab in the URL so a refresh keeps the learner on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get('tab') === 'requests' ? 'requests' : 'record';
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestPrefill, setRequestPrefill] = useState<RequestPrefill | null>(
    null
  );

  // Courses and credits come from ALL non-cancelled enrollments: when a
  // learner changes specialization (programId change or withdraw + re-enroll),
  // the graded courses taken under the former program stay on the transcript
  // and keep counting toward the GPA. Mirrors the official snapshot.
  const transcriptSourceEnrollments = useMemo(
    () => programEnrollments.filter((e) => !e.cancelled),
    [programEnrollments]
  );

  // Only ongoing/graduated enrollments are listed in the header and provide
  // the GPA / term / conduct data. A learner on leave of absence counts as
  // ongoing — the leave pauses study but must not hide the record they have
  // already earned. DEFERRED is excluded on purpose: a deferred learner never
  // started, so there is no coursework to transcribe. Mirrors the same filter in
  // the staff TranscriptTab and in the server-side transcript snapshot.
  const visibleEnrollments = useMemo(
    () =>
      programEnrollments.filter(
        (e) =>
          e.status === 'ONGOING' ||
          e.status === 'LEAVE_OF_ABSENCE' ||
          e.status === 'GRADUATED'
      ),
    [programEnrollments]
  );

  const courseEnrollments = useMemo(
    () => collectCourseEnrollments(transcriptSourceEnrollments),
    [transcriptSourceEnrollments]
  );

  const creditsByCourseDef = useMemo(
    () => buildCreditMap(transcriptSourceEnrollments),
    [transcriptSourceEnrollments]
  );

  const primaryEnrollment = useMemo(
    () =>
      visibleEnrollments.find(
        (e) => e.status === 'ONGOING' || e.status === 'LEAVE_OF_ABSENCE'
      ) ?? visibleEnrollments[0],
    [visibleEnrollments]
  );

  const conductCases: TranscriptConductCase[] = useMemo(() => {
    const cases: TranscriptConductCase[] = [];
    for (const pe of visibleEnrollments) {
      for (const c of pe.academicConductCases) {
        cases.push({
          severity: c.severity,
          category: c.category,
          status: c.status,
          resolutionType: c.resolutionType,
          term: c.term,
          className: c.className ?? null,
        });
      }
    }
    return cases;
  }, [visibleEnrollments]);

  const termGPAs: TranscriptTermGPA[] = useMemo(
    () =>
      (primaryEnrollment?.termGPAs ?? []).map((t) => ({
        term: t.term,
        gpa: t.gpa,
      })),
    [primaryEnrollment]
  );

  const transcriptProgramEnrollments: TranscriptProgramEnrollment[] = useMemo(
    () =>
      visibleEnrollments.map((e) => ({
        status: e.status,
        graduatedAt: e.graduatedAt,
        program: { name: e.program.name ?? '' },
      })),
    [visibleEnrollments]
  );

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-5 md:gap-9 w-full">
      {/* Page title bar with the primary action (visible from both tabs) */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full print:hidden">
        <Button
          variant="outlineFg"
          className="rounded-full gap-2"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          variant="outline"
          className="rounded-full border-primary text-primary hover:bg-primary/5 px-6 md:px-8 h-10 text-sm gap-2"
          onClick={() => {
            setRequestPrefill(null);
            setRequestModalOpen(true);
          }}
        >
          <Send className="w-4 h-4" />
          Request official transcript
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setSearchParams(
            (prev) => {
              prev.set('tab', value);
              return prev;
            },
            { replace: true }
          )
        }
        className="w-full flex flex-col gap-5 md:gap-8"
      >
        <TabsList className="h-auto bg-transparent border-b border-muted rounded-none p-0 justify-start gap-6 print:hidden">
          <TabsTrigger
            value="record"
            className="data-[state=active]:border-primary data-[state=active]:border-b-2 data-[state=active]:text-foreground text-muted-foreground font-semibold rounded-none px-1 pb-3"
          >
            Academic Record
          </TabsTrigger>
          <TabsTrigger
            value="requests"
            className="data-[state=active]:border-primary data-[state=active]:border-b-2 data-[state=active]:text-foreground text-muted-foreground font-semibold rounded-none px-1 pb-3"
          >
            Official Transcript Requests
          </TabsTrigger>
        </TabsList>

        {/* forceMount keeps the record in the DOM even when the Requests tab
            is active, so a native browser print (Cmd/Ctrl+P) from any tab
            still captures the transcript. It is hidden on screen while
            inactive, and print:!block overrides that hide so it prints. */}
        <TabsContent
          value="record"
          forceMount
          className="mt-0 flex flex-col gap-5 print:gap-0 data-[state=inactive]:hidden print:!block"
        >
          <div className="flex justify-end print:hidden">
            <Button
              className="rounded-full px-6 md:px-8 h-10 text-sm gap-2"
              onClick={() => window.print()}
            >
              <Download className="w-4 h-4" />
              Download as PDF
            </Button>
          </div>
          {/* Transcript card (shared component) */}
          <TranscriptView
            user={viewer}
            programEnrollments={transcriptProgramEnrollments}
            courseEnrollments={courseEnrollments}
            creditsByCourseDef={creditsByCourseDef}
            cumulativeGPA={primaryEnrollment?.cumulativeGPA ?? null}
            termGPAs={termGPAs}
            conductCases={conductCases}
          />
        </TabsContent>

        {/* Never part of a printed transcript, even when this tab is active. */}
        <TabsContent value="requests" className="mt-0 print:hidden">
          <TranscriptRequestsStatus
            onResubmit={(prefill) => {
              setRequestPrefill(prefill);
              setRequestModalOpen(true);
            }}
          />
        </TabsContent>
      </Tabs>

      <RequestOfficialTranscriptModal
        open={requestModalOpen}
        onOpenChange={setRequestModalOpen}
        viewer={viewer}
        prefill={requestPrefill}
      />
    </div>
  );
};

export default TranscriptContent;
